import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Sparkles, Save, Trash2, Loader2, Wand2 } from 'lucide-react';
import { extrajudicialAPI } from '../api';
import { toast } from '../hooks/use-toast';

const AsistenteArgumentosIA = ({ reclamacionId }) => {
  const [catalogo, setCatalogo] = useState([]);
  const [textos, setTextos] = useState({});
  const [modelos, setModelos] = useState({});
  const [provider, setProvider] = useState('');
  const [model, setModel] = useState('');
  const [loadingKey, setLoadingKey] = useState(null);
  const [savingKey, setSavingKey] = useState(null);

  useEffect(() => { if (reclamacionId) load(); /* eslint-disable-next-line */ }, [reclamacionId]);

  const load = async () => {
    try {
      const [cat, saved, prov] = await Promise.all([
        extrajudicialAPI.argumentosCatalogo(reclamacionId),
        extrajudicialAPI.argumentosList(reclamacionId),
        extrajudicialAPI.iaProviders().catch(() => ({ data: null })),
      ]);
      setCatalogo(cat.data || []);
      const t = {};
      (saved.data || []).forEach((s) => { t[s.clausula_key] = s.texto || ''; });
      setTextos(t);
      if (prov && prov.data) {
        setModelos(prov.data.modelos || {});
        setProvider(prov.data.default_provider || '');
        setModel(prov.data.default_model || '');
      }
    } catch (e) { /* noop */ }
  };

  const onProviderChange = (p) => {
    setProvider(p);
    const list = modelos[p] || [];
    setModel(list[0] || '');
  };

  const sugerir = async (ck) => {
    setLoadingKey(ck);
    try {
      const r = await extrajudicialAPI.argumentoSugerir(reclamacionId, { clausula_key: ck, provider, model });
      setTextos((prev) => ({ ...prev, [ck]: r.data.texto || '' }));
      toast({ title: 'Argumento generado', description: 'Revísalo y pulsa Guardar.' });
    } catch (e) {
      toast({ title: 'Error', description: (e && e.response && e.response.data && e.response.data.detail) || 'No se pudo generar', variant: 'destructive' });
    } finally { setLoadingKey(null); }
  };

  const guardar = async (ck) => {
    setSavingKey(ck);
    try {
      await extrajudicialAPI.argumentoGuardar(reclamacionId, ck, { texto: textos[ck] || '', modelo_ia: (provider && model) ? (provider + '/' + model) : null });
      toast({ title: 'Guardado', description: 'El argumento se insertará en el Word mediante su marcador.' });
    } catch (e) {
      toast({ title: 'Error', description: 'No se pudo guardar', variant: 'destructive' });
    } finally { setSavingKey(null); }
  };

  const borrar = async (ck) => {
    try {
      await extrajudicialAPI.argumentoBorrar(reclamacionId, ck);
      setTextos((prev) => ({ ...prev, [ck]: '' }));
      toast({ title: 'Eliminado' });
    } catch (e) { toast({ title: 'Error', variant: 'destructive' }); }
  };

  const providers = Object.keys(modelos);

  return (
    <Card data-testid="asistente-argumentos-ia">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Wand2 className="w-5 h-5 text-purple-600" />
          <CardTitle className="text-base">Asistente de Argumentos IA</CardTitle>
        </div>
        <CardDescription>
          Genera el argumento jurídico de cada cláusula leyendo la escritura ya analizada. El texto es editable y, al guardarlo,
          se inserta en el Word de la reclamación mediante su marcador.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Proveedor</label>
            <select data-testid="arg-provider-select" value={provider} onChange={(e) => onProviderChange(e.target.value)} className="border rounded-md px-2 py-1.5 text-sm bg-white">
              {providers.length === 0 && <option value="">(por defecto)</option>}
              {providers.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Modelo</label>
            <select data-testid="arg-model-select" value={model} onChange={(e) => setModel(e.target.value)} className="border rounded-md px-2 py-1.5 text-sm bg-white">
              {(modelos[provider] || []).length === 0 && <option value="">(por defecto)</option>}
              {(modelos[provider] || []).map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
        </div>

        <div className="space-y-4">
          {catalogo.map((c) => (
            <div key={c.clausula_key} className="border rounded-lg p-3 space-y-2" data-testid={`arg-bloque-${c.clausula_key}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold">{c.label}</span>
                  <code className="text-xs text-blue-600 bg-blue-50 px-1 rounded">{c.marcador}</code>
                </div>
                <Button size="sm" variant="outline" disabled={loadingKey === c.clausula_key} onClick={() => sugerir(c.clausula_key)} data-testid={`arg-sugerir-${c.clausula_key}`}>
                  {loadingKey === c.clausula_key ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Sparkles className="w-4 h-4 mr-1" />}
                  Sugerir (IA)
                </Button>
              </div>
              <Textarea rows={5} value={textos[c.clausula_key] || ''} onChange={(e) => setTextos((prev) => ({ ...prev, [c.clausula_key]: e.target.value }))} placeholder="Genera con IA o escribe aquí el argumento…" data-testid={`arg-textarea-${c.clausula_key}`} />
              <div className="flex gap-2">
                <Button size="sm" disabled={savingKey === c.clausula_key} onClick={() => guardar(c.clausula_key)} data-testid={`arg-guardar-${c.clausula_key}`}>
                  {savingKey === c.clausula_key ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                  Guardar
                </Button>
                <Button size="sm" variant="outline" className="text-red-600" onClick={() => borrar(c.clausula_key)} data-testid={`arg-borrar-${c.clausula_key}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default AsistenteArgumentosIA;
